import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, Typography, useMediaQuery, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DndContext, PointerSensor, TouchSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { DragVerticalIcon } from '@ui/assets/custom-icons/drag-vertical-icon';
import CustomDialog from '@ui/components/common/Dialog';
import BDrawer from '@ui/components/common/BDrawer';
import { ControlledFilledTextField } from '@ui/components/common/TextField';
import ControlledHCheckboxAutocomplete from '@ui/components/common/Autocomplete/ControlledHCheckboxAutocomplete';
import SingleSelect from '@ui/components/common/Autocomplete/SingleSelect';
import { GoogleMap, Marker, OverlayView, OverlayViewF, Polyline, useJsApiLoader } from '@react-google-maps/api';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { getMapsKeyAsync } from '@application/features/pickupLocation/pickupLocationThunk';
import { CateringPackageEnum, CateringPackageEnumDisplay } from '@application/enums/catering-package-enum';
const RoutePreviewMap = ({ apiKey, mapCenter, routeLocations }) => {
    const { t } = useTranslation();
    const [hoveredMarkerId, setHoveredMarkerId] = useState(null);
    const { isLoaded: isMapLoaded } = useJsApiLoader({
        id: 'transport-dashboard-google-map',
        googleMapsApiKey: apiKey
    });
    const routeCoordinates = useMemo(() => routeLocations.map((location) => location.position), [routeLocations]);
    const hoveredLocation = useMemo(() => routeLocations.find((location) => location.id === hoveredMarkerId) ?? null, [hoveredMarkerId, routeLocations]);
    if (!isMapLoaded) {
        return (_jsx(Box, { sx: {
                height: 331,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 2
            }, children: _jsx(Typography, { variant: 'body2', color: 'text.secondary', children: t('events.eventDetails.transportTab.loadingMap') }) }));
    }
    return (_jsxs(GoogleMap, { mapContainerStyle: { width: '100%', height: '331px' }, center: mapCenter, zoom: routeCoordinates.length > 0 ? 7 : 6, options: {
            streetViewControl: false,
            fullscreenControl: false,
            mapTypeControl: false
        }, children: [routeLocations.map((location, index) => (_jsx(Marker, { position: location.position, label: { text: `${index + 1}`, color: 'white', fontWeight: 'bold' }, onMouseOver: () => setHoveredMarkerId(location.id), onMouseOut: () => setHoveredMarkerId((current) => (current === location.id ? null : current)) }, `route-marker-${location.id}`))), hoveredLocation && (_jsx(OverlayViewF, { position: hoveredLocation.position, mapPaneName: OverlayView.FLOAT_PANE, children: _jsxs(Box, { sx: {
                        transform: 'translate(12px, -14px)',
                        bgcolor: 'rgba(255,255,255,0.95)',
                        borderRadius: '8px',
                        px: 1.25,
                        py: 0.75,
                        boxShadow: (themeObj) => themeObj.customShadows?.z8 || themeObj.shadows[3],
                        border: (themeObj) => `1px solid ${themeObj.palette.divider}`,
                        minWidth: 220
                    }, children: [_jsx(Typography, { variant: 'body2', sx: { fontWeight: 600, lineHeight: 1.2 }, children: hoveredLocation.label }), _jsx(Typography, { variant: 'caption', color: 'text.secondary', sx: { lineHeight: 1.2 }, children: hoveredLocation.address })] }) })), routeCoordinates.length > 1 && (_jsx(Polyline, { path: routeCoordinates, options: {
                    strokeColor: '#1E88E5',
                    strokeOpacity: 0.95,
                    strokeWeight: 3
                } }))] }));
};
const SortableSelectedPickupLocationRow = ({ location }) => {
    const { t } = useTranslation();
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
        id: location.value
    });
    const attendeesCount = Number(location.attendeesCount ?? 0);
    const style = { transform: CSS.Transform.toString(transform), transition };
    return (_jsxs(Box, { ref: setNodeRef, style: style, sx: { display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, py: 1 }, children: [_jsx(Box, { ...attributes, ...listeners, sx: {
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'grab',
                    touchAction: 'none'
                }, children: _jsx(DragVerticalIcon, {}) }), _jsxs(Box, { sx: { minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1 }, children: [_jsx(Typography, { variant: 'body2', noWrap: true, sx: { textOverflow: 'ellipsis', overflow: 'hidden' }, children: location.label }), _jsx(Typography, { variant: 'caption', color: 'text.secondary', noWrap: true, sx: { textOverflow: 'ellipsis', overflow: 'hidden' }, children: location.location || '—' })] }), attendeesCount > 0 && (_jsx(Box, { children: _jsx(Chip, { label: t('events.eventDetails.routeTab.attendeesCount', { count: attendeesCount }), size: 'small', variant: 'soft', color: 'default' }) }))] }));
};
const CreateRouteDialog = ({ open, onClose, pickupLocationOptions, vehicleOptions, editingRoute, onCreate }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    const dispatch = useAppDispatch();
    const mapsKey = useAppSelector((state) => state.pickupLocation.mapsKey);
    const { control, handleSubmit, reset, setValue, formState: { errors } } = useForm({
        defaultValues: {
            routeName: '',
            cateringPackage: null,
            pickupLocations: [],
            vehicles: []
        }
    });
    const cateringPackageOptions = useMemo(() => Object.values(CateringPackageEnum).filter((v) => typeof v === 'number').map((pkg) => ({ value: String(pkg), label: t(CateringPackageEnumDisplay[pkg]) })), [t]);
    const selectedPickupLocations = useWatch({ control, name: 'pickupLocations', defaultValue: [] });
    const orderedSelectedLocations = useMemo(() => (Array.isArray(selectedPickupLocations) ? selectedPickupLocations : []), [selectedPickupLocations]);
    const routeLocations = useMemo(() => orderedSelectedLocations
        .map((location, index) => {
        const loc = location;
        if (!loc.latitude || !loc.longitude)
            return null;
        return {
            id: String(location.value ?? index),
            label: location.label,
            address: location.location || '—',
            position: { lat: loc.latitude, lng: loc.longitude }
        };
    })
        .filter(Boolean), [orderedSelectedLocations]);
    const mapCenter = useMemo(() => routeLocations[0]?.position ?? { lat: 52.1326, lng: 5.2913 }, [routeLocations]);
    useEffect(() => {
        if (!mapsKey?.key) {
            dispatch(getMapsKeyAsync());
        }
    }, [dispatch, mapsKey?.key]);
    useEffect(() => {
        if (!open)
            return;
        if (editingRoute) {
            reset({
                routeName: editingRoute.routeName,
                cateringPackage: editingRoute.cateringPackage != null ? String(editingRoute.cateringPackage) : null,
                pickupLocations: editingRoute.pickupLocations ?? [],
                vehicles: editingRoute.vehicles ?? []
            });
            return;
        }
        reset({ routeName: '', cateringPackage: null, pickupLocations: [], vehicles: [] });
    }, [open, editingRoute, reset]);
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }), useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }));
    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id)
            return;
        const oldIndex = orderedSelectedLocations.findIndex((item) => item.value === active.id);
        const newIndex = orderedSelectedLocations.findIndex((item) => item.value === over.id);
        if (oldIndex < 0 || newIndex < 0)
            return;
        setValue('pickupLocations', arrayMove(orderedSelectedLocations, oldIndex, newIndex), {
            shouldDirty: true
        });
    };
    const handleCancel = () => {
        reset({ routeName: '', cateringPackage: null, pickupLocations: [], vehicles: [] });
        onClose();
    };
    const onSubmit = async (values) => {
        const success = await onCreate({ ...values, orderedPickupLocations: values.pickupLocations });
        if (!success)
            return;
        reset({ routeName: '', cateringPackage: null, pickupLocations: [], vehicles: [] });
        onClose();
    };
    const dialogTitle = editingRoute
        ? t('events.eventDetails.routeTab.editRoute')
        : t('events.eventDetails.routeTab.createNewRoute');
    const formContent = (_jsxs(Grid, { container: true, spacing: '12px', children: [_jsx(Grid, { size: { xs: 12, md: 6 }, children: _jsx(ControlledFilledTextField, { bg: 'grey8', control: control, name: 'routeName', label: t('events.eventDetails.routeTab.routeName'), rules: {
                        required: t('validations.required', {
                            field: t('events.eventDetails.routeTab.routeName')
                        })
                    } }) }), _jsx(Grid, { size: { xs: 12, md: 6 }, children: _jsx(SingleSelect, { control: control, name: 'cateringPackage', label: t('events.eventDetails.routeTab.cateringPackage'), bg: 'grey8', options: cateringPackageOptions }) }), _jsx(Grid, { size: { xs: 12, md: 6 }, children: _jsx(ControlledHCheckboxAutocomplete, { control: control, name: 'pickupLocations', label: t('events.eventDetails.routeTab.pickupLocations'), bg: 'grey8', options: pickupLocationOptions, showCountTextWhenMultiple: true, showLocation: true, isSortable: true, error: errors.pickupLocations?.message, rules: {
                        validate: (value) => (Array.isArray(value) && value.length > 0) ||
                            t('validations.required', {
                                field: t('events.eventDetails.routeTab.pickupLocations')
                            })
                    } }) }), _jsx(Grid, { size: { xs: 12, md: 6 }, children: _jsx(ControlledHCheckboxAutocomplete, { control: control, name: 'vehicles', label: t('events.eventDetails.routeTab.vehicles'), bg: 'grey8', options: vehicleOptions, showCountTextWhenMultiple: true, showName: true, isSortable: true }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsxs(Box, { display: 'flex', flexDirection: 'column', gap: 0.5, children: [_jsx(Typography, { variant: 'subtitle1', children: t('events.eventDetails.routeTab.selectedPickupLocationsOrder') }), _jsx(Typography, { variant: 'body2', color: 'text.secondary', children: t('events.eventDetails.routeTab.dragToChangeOrder') })] }) }), _jsx(Grid, { size: { xs: 12, md: 6 }, children: _jsx(Box, { sx: { borderRadius: 2, pr: 1, overflow: 'hidden' }, children: _jsx(Box, { sx: { maxHeight: { xs: 260, lg: 332 }, overflowY: 'auto', pr: 1 }, children: _jsx(DndContext, { sensors: sensors, collisionDetection: closestCenter, modifiers: [restrictToVerticalAxis, restrictToParentElement], onDragEnd: handleDragEnd, children: _jsx(SortableContext, { items: orderedSelectedLocations.map((item) => item.value), strategy: verticalListSortingStrategy, children: _jsx(Box, { display: 'flex', flexDirection: 'column', gap: 0.5, children: orderedSelectedLocations.map((location) => (_jsx(SortableSelectedPickupLocationRow, { location: location }, location.value))) }) }) }) }) }) }), _jsx(Grid, { size: { xs: 12, md: 6 }, children: _jsx(Box, { sx: {
                        borderRadius: 3,
                        overflow: 'hidden',
                        border: (themeObj) => `1px solid ${themeObj.palette.divider}`,
                        minHeight: 331,
                        position: 'relative'
                    }, children: !mapsKey?.key ? (_jsx(Box, { sx: {
                            height: 331,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            px: 2
                        }, children: _jsx(Typography, { variant: 'body2', color: 'text.secondary', children: t('events.eventDetails.transportTab.loadingMapKey') }) })) : (_jsx(RoutePreviewMap, { apiKey: mapsKey.key, mapCenter: mapCenter, routeLocations: routeLocations })) }) })] }));
    const actions = (_jsxs(_Fragment, { children: [_jsx(Button, { variant: 'outlined', color: 'inherit', size: 'large', onClick: handleCancel, fullWidth: true, children: t('actions.cancel') }), _jsx(Button, { variant: 'contained', color: 'inherit', size: 'large', onClick: handleSubmit(onSubmit), fullWidth: true, children: editingRoute ? t('actions.saveChanges') : t('events.eventDetails.routeTab.createNow') })] }));
    if (isSmallScreen) {
        return (_jsx(BDrawer, { open: open, onClose: handleCancel, title: dialogTitle, anchor: 'bottom', showBottomBar: true, dialogActions: actions, children: _jsx(Box, { sx: { p: 2 }, children: formContent }) }));
    }
    return (_jsx(CustomDialog, { open: open, onClose: handleCancel, titleWithIcon: dialogTitle, titleIcon: '/assets/images/setting-task-icon.png', maxWidth: 'md', backgroundVariant: 'white', closeIcon: true, actions: actions, children: _jsx(Box, { p: 1.5, sx: { maxHeight: '70vh', overflowY: 'auto' }, children: formContent }) }));
};
export default CreateRouteDialog;
